/**
 * Log a call, on any person: pick the outcome, add a note, Save (⌘Enter). It is on the timeline at once and written
 * after six seconds, so Undo (in the toast, or ⌘Z) leaves nothing in the record and brings the call back to the
 * sheet. On a queue's case it is filed under the queue, and Mark reviewed can carry it instead.
 */
import { useRef } from "react";
import { createPersonOutreachEntry } from "../../api/outreach";
import { invalidate } from "../data/useQuery";
import { Button, Field, Kbd, modKey, SheetSection, Textarea, useToast } from "../kit";
import { hold, UNDO_MS } from "./hold";
import { addPending } from "./pending";
import { callSummary, NO_CALL, OUTCOMES, type CallDraft } from "./review";

interface LogCallProps {
  personId: number;
  /** The queue whose case the sheet is on; without one the call is a touchpoint on the person. */
  queueKey?: string;
  draft: CallDraft;
  onDraft: (draft: CallDraft) => void;
  /** A call that came back (Undo, or it could not be written) to the person it was logged on: the sheet keeps it only if it is still on that person. */
  onRestore: (personId: number, call: CallDraft) => void;
}

export function LogCall({ personId, queueKey, draft, onDraft, onRestore }: LogCallProps) {
  const toast = useToast();
  const group = useRef<HTMLDivElement>(null);

  /** A second press on the picked outcome takes it back: a mis-click is undone without a write. */
  const pick = (outcome: (typeof OUTCOMES)[number]) => onDraft({ ...draft, outcome: draft.outcome === outcome ? null : outcome });

  const save = () => {
    if (!draft.outcome) return;
    const call = draft;
    const unshow = addPending(personId, "call", callSummary(call));
    const restore = () => {
      unshow();
      onRestore(personId, call);
    };
    const held = hold(() => {
      createPersonOutreachEntry(personId, { channel: "call", summary: callSummary(call), ...(queueKey && { queue_key: queueKey }) }).then(
        () => {
          void invalidate(`v2:person:${personId}`).finally(unshow);
          void invalidate("people:");
        },
        () => {
          toast.show({ message: "Could not save the call" });
          restore();
        },
      );
    });
    onDraft(NO_CALL);
    toast.show({
      message: "Call logged",
      action: {
        label: "Undo",
        onAction: () => {
          held.cancel();
          restore();
        },
      },
      duration: UNDO_MS,
      onExpire: held.flush,
    });
    // The note and Save are gone: focus goes back to the outcomes, not to the page.
    group.current?.querySelector("button")?.focus();
  };

  return (
    <SheetSection title="Log a call">
      <div ref={group} role="group" aria-label="Log a call" className="flex gap-2">
        {OUTCOMES.map((outcome) => {
          const pressed = draft.outcome === outcome;
          return (
            <Button key={outcome} variant={pressed ? "primary" : "surface"} size="sm" aria-pressed={pressed} onClick={() => pick(outcome)}>
              {outcome}
            </Button>
          );
        })}
      </div>
      {draft.outcome && (
        <div className="flex w-full animate-fade flex-col gap-2">
          <Field label="Note" hideLabel>
            <Textarea
              rows={2}
              placeholder="Note"
              value={draft.note}
              onChange={(event) => onDraft({ ...draft, note: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  save();
                }
              }}
            />
          </Field>
          <Button variant="surface" size="sm" className="self-end" aria-keyshortcuts="Meta+Enter Control+Enter" onClick={save}>
            Save
            <span aria-hidden>
              <Kbd>{modKey("↵")}</Kbd>
            </span>
          </Button>
        </div>
      )}
    </SheetSection>
  );
}
